<template>
  <div class="ai-preparation-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 训练课文按钮 -->
      <div class="sub-button-group">
        <button class="sub-button orange-button" @click="handleAddTraining">
          训练课文
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 数据表格区域 -->
        <div class="table-content">
          <!-- 顶部信息栏 -->
          <div class="info-bar">
            <div class="info-item">
              <span class="info-label">{{ getTrainingTypeLabel() }}可发条数：</span>
              <span class="info-value">{{ trainingStats.developCount }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">到期时间：</span>
              <span class="info-value">{{ trainingStats.expireTime }}</span>
            </div>
          </div>

          <!-- 数据表格 -->
          <el-table :data="tableData" v-loading="loading" class="data-table" :header-cell-style="{
            background: 'transparent',
            color: '#e0e6ff',
            borderBottom: '1px solid rgba(255, 112, 72, 0.3)'
          }">
            <el-table-column type="index" label="序号" width="60" />
            <el-table-column prop="task_name" label="任务名称" min-width="120" />
            <el-table-column prop="training_type_text" label="类型" width="120">
              <template #default="{ row }">
                <el-tag effect="dark" class="type-tag">
                  {{ row.training_type_text }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="keyword_package_name" label="关键词包" min-width="150">
              <template #default="{ row }">
                <div class="keywords-content">
                  {{ row.keyword_package_name || '-' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="portrait_name" label="画像" min-width="120">
              <template #default="{ row }">
                {{ row.portrait_name || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="training_count" label="训练课文数量" width="120">
              <template #default="{ row }">
                <span class="count-text">{{ row.training_count }}篇</span>
              </template>
            </el-table-column>
            <el-table-column prop="generated_count" label="已生成数量" width="110">
              <template #default="{ row }">
                <span class="count-text">{{ row.generated_count }}篇</span>
              </template>
            </el-table-column>
            <el-table-column prop="status_text" label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)" effect="dark">
                  {{ row.status_text }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="时间" width="140" />
            <el-table-column label="操作" fixed="right" width="200">
              <template #default="{ row }">
                <el-button size="small" @click="handleViewPublish(row)" class="action-btn">
                  查看发布
                </el-button>
                <el-button size="small" type="danger" @click="handleDelete(row)" class="action-btn">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
              :page-sizes="[10, 20, 50, 100]" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange" @current-change="handleCurrentChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 添加训练任务对话框 -->
    <el-dialog v-model="showAddDialog" :title="dialogTitle" width="600px" :close-on-click-modal="false"
      class="training-dialog">
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="120px">
        <!-- 配额信息显示 -->
        <div class="quota-info">
          <div class="quota-item">
            <span class="quota-label">{{ getTrainingTypeLabel() }}可发条数：</span>
            <span class="quota-value">{{ trainingStats.developCount }}</span>
          </div>
          <div class="quota-item">
            <span class="quota-label">到期时间：</span>
            <span class="quota-value">{{ trainingStats.expireTime }}</span>
          </div>
        </div>

        <el-form-item label="任务名称" prop="task_name">
          <el-input v-model="formData.task_name" placeholder="请输入任务名称" />
        </el-form-item>

        <el-form-item label="关键词包" prop="keyword_package_id">
          <el-select v-model="formData.keyword_package_id" placeholder="请选择关键词包" class="full-width-select"
            :loading="keywordPackagesLoading">
            <el-option v-for="pkg in keywordPackages" :key="pkg.id"
              :label="`${pkg.package_name} (${pkg.keyword_count}个关键词)`" :value="pkg.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="画像" prop="portrait_id">
          <el-select v-model="formData.portrait_id" placeholder="请选择画像" class="full-width-select"
            :loading="portraitsLoading">
            <el-option v-for="portrait in portraits" :key="portrait.id" :label="portrait.portrait_name"
              :value="portrait.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="图库" prop="library_id">
          <el-select v-model="formData.library_id" placeholder="请选择图库" class="full-width-select"
            :loading="librariesLoading">
            <el-option v-for="library in libraries" :key="library.id" :label="library.tuku_name" :value="library.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="训练数量" prop="training_count">
          <el-input-number v-model="formData.training_count" :min="1" :max="30" />
          <span class="unit-text">篇</span>
        </el-form-item>

        <el-form-item label="使用共享池账号" prop="use_shared_pool">
          <el-switch v-model="formData.use_shared_pool" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="showAddDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">
          开始训练
        </el-button>
      </template>
    </el-dialog>

    <!-- 文章列表对话框 -->
    <el-dialog v-model="showArticleDialog" :title="`${selectedTask?.task_name} - 文章列表`" width="90%"
      class="article-dialog" :close-on-click-modal="false">
      <div class="article-header">
        <div class="article-stats">
          <span>共 {{ articlePagination.total }} 篇文章</span>
          <el-button type="primary" size="small" @click="handleBatchPublish" 
            :disabled="selectedArticles.length === 0 || !isBatchPublishable">
            批量发布 ({{ selectedArticles.length }})
          </el-button>
        </div>
      </div>

      <el-table :data="articleList" v-loading="articleLoading" class="article-table"
        @selection-change="handleArticleSelectionChange">
        <el-table-column type="selection" width="55" />
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="title" label="标题" min-width="200">
          <template #default="{ row }">
            <div class="article-title" @click="handleEditArticle(row)">
              {{ row.title }}
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="conversion_status_text" label="转换状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getConversionStatusType(row.conversion_status)" size="small">
              {{ row.conversion_status_text }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status_text" label="发布状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getPublishStatusType(row.status)" size="small">
              {{ row.status_text }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="create_time" label="创建时间" width="140" />
        <el-table-column label="操作" width="200">
          <template #default="{ row }">
            <el-button size="small" @click="handleEditArticle(row)">编辑</el-button>
            <el-button size="small" type="primary" @click="handlePublishArticle(row)" 
              :disabled="!isArticlePublishable(row)" :loading="row.publishing">
              {{ getPublishButtonText(row) }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 文章分页 -->
      <div class="pagination">
        <el-pagination v-model:current-page="articlePagination.page" v-model:page-size="articlePagination.pageSize"
          :page-sizes="[10, 20, 50]" :total="articlePagination.total" layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleArticleSizeChange" @current-change="handleArticleCurrentChange" />
      </div>
    </el-dialog>

    <!-- 文章编辑对话框 -->
    <el-dialog v-model="showEditDialog" title="编辑文章" width="90%" class="edit-dialog" :close-on-click-modal="false">
      <div class="edit-container">
        <div class="edit-form">
          <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="80px">
            <el-form-item label="文章标题" prop="title">
              <el-input v-model="editForm.title" placeholder="请输入文章标题" />
            </el-form-item>
          </el-form>
        </div>

        <div class="editor-container">
          <mavon-editor v-model="editForm.content" :toolbars="markdownOption" style="height: 500px;" ref="mdEditor"
            @imgAdd="handleImageAdd" />
        </div>
      </div>

      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" @click="handleSaveArticle" :loading="savingArticle">
          保存文章
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'
import { mavonEditor } from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'

export default {
  name: 'AiPreparation',
  components: {
    mavonEditor
  },
  data() {
    return {
      // 响应式数据
      activeMainButton: 'self_media',
      loading: false,
      showAddDialog: false,
      showArticleDialog: false,
      showEditDialog: false,
      submitting: false,
      savingArticle: false,
      formRef: null,
      editFormRef: null,

      // 主按钮配置
      mainButtons: [
        { id: 'self_media', label: '自媒体训练' },
        { id: 'third_media', label: '第三方媒体训练' },
        { id: 'b2b', label: 'B2B训练' },
        { id: 'ai_agent', label: '智能体官网训练' }
      ],

      // 训练统计
      trainingStats: {
        developCount: 0,
        expireTime: ''
      },

      // 分页数据
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 表格数据
      tableData: [],

      // 表单数据
      formData: {
        task_name: '',
        keyword_package_id: '',
        portrait_id: '',
        library_id: '',
        training_count: 20,
        use_shared_pool: 0
      },

      // 关键词包列表
      keywordPackages: [],
      keywordPackagesLoading: false,

      // 画像列表
      portraits: [],
      portraitsLoading: false,

      // 图库列表
      libraries: [],
      librariesLoading: false,

      // 文章相关数据
      selectedTask: null,
      articleList: [],
      articleLoading: false,
      selectedArticles: [],

      // 文章分页
      articlePagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },

      // 编辑表单
      editForm: {
        article_id: null,
        title: '',
        content: ''
      },

      // Markdown 编辑器配置
      markdownOption: {
        bold: true,
        italic: true,
        header: true,
        underline: true,
        strikethrough: true,
        mark: true,
        superscript: true,
        subscript: true,
        quote: true,
        ol: true,
        ul: true,
        link: true,
        imagelink: true,
        code: true,
        table: true,
        fullscreen: true,
        readmodel: true,
        htmlcode: true,
        help: true,
        undo: true,
        redo: true,
        trash: true,
        save: true,
        navigation: true,
        alignleft: true,
        aligncenter: true,
        alignright: true,
        subfield: true,
        preview: true
      },

      // 表单验证规则
      formRules: {
        task_name: [
          { required: true, message: '请输入任务名称', trigger: 'blur' }
        ],
        keyword_package_id: [
          { required: true, message: '请选择关键词包', trigger: 'change' }
        ],
        portrait_id: [
          { required: true, message: '请选择画像', trigger: 'change' }
        ],
        library_id: [
          { required: true, message: '请选择图库', trigger: 'change' }
        ],
        training_count: [
          { required: true, message: '请输入训练数量', trigger: 'blur' }
        ]
      },

      // 编辑表单验证规则
      editRules: {
        title: [
          { required: true, message: '请输入文章标题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入文章内容', trigger: 'blur' }
        ]
      },

      // 发布状态管理
      publishingArticles: new Set()
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('store_user_info') || '{}')
    },
    userId() {
      return this.userInfo.id
    },
    storeId() {
      return this.userInfo.store_id
    },
    dialogTitle() {
      return `添加${this.getTrainingTypeLabel()}任务`
    },
    // 检查批量发布是否可用
    isBatchPublishable() {
      return this.selectedArticles.some(article => this.isArticlePublishable(article))
    }
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId
      this.loadTableData()
      this.loadUserQuota()
    },

    handleAddTraining() {
      this.showAddDialog = true
      this.formData = {
        task_name: '',
        keyword_package_id: '',
        portrait_id: '',
        library_id: '',
        training_count: 20,
        use_shared_pool: 0
      }

      // 加载用户数据
      this.loadUserQuota()
      this.loadKeywordPackages()
      this.loadPortraits()
      this.loadLibraries()

      // 清除表单验证状态
      this.$nextTick(() => {
        if (this.$refs.formRef) {
          this.$refs.formRef.clearValidate()
        }
      })
    },

    // 加载用户配额信息
    async loadUserQuota() {
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'user_list',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (response.data.success && response.data.data.length > 0) {
          const userData = response.data.data[0]
          const trainingTypeMap = {
            'self_media': 'self_media_count',
            'third_media': 'third_party_media_count',
            'b2b': 'b2b_count',
            'ai_agent': 'ai_agent_website_count'
          }

          const quotaField = trainingTypeMap[this.activeMainButton]
          const quota = userData[quotaField]

          // 更新训练统计
          this.trainingStats = {
            developCount: quota === -1 ? '无限' : quota,
            expireTime: userData.out_date ? this.formatTime(userData.out_date) : '永久'
          }
        }
      } catch (error) {
        console.error('加载用户配额失败:', error)
      }
    },

    // 加载关键词包
    async loadKeywordPackages() {
      this.keywordPackagesLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'keyword_packages',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (response.data.success) {
          this.keywordPackages = response.data.data
        }
      } catch (error) {
        console.error('加载关键词包失败:', error)
        ElMessage.error('加载关键词包失败')
      } finally {
        this.keywordPackagesLoading = false
      }
    },

    // 加载画像
    async loadPortraits() {
      this.portraitsLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'portraits',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (response.data.success) {
          this.portraits = response.data.data
        }
      } catch (error) {
        console.error('加载画像失败:', error)
        ElMessage.error('加载画像失败')
      } finally {
        this.portraitsLoading = false
      }
    },

    // 加载图库
    async loadLibraries() {
      this.librariesLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'libraries',
            store_id: this.storeId,
            user_id: this.userId
          }
        )

        if (response.data.success) {
          this.libraries = response.data.data
        }
      } catch (error) {
        console.error('加载图库失败:', error)
        ElMessage.error('加载图库失败')
      } finally {
        this.librariesLoading = false
      }
    },

    getTrainingTypeLabel() {
      const button = this.mainButtons.find(b => b.id === this.activeMainButton)
      return button ? button.label : ''
    },

    getStatusType(status) {
      const statusMap = {
        0: 'info',    // 未开始
        1: 'warning', // 生成中
        2: 'success', // 已完成
        3: 'danger'   // 失败
      }
      return statusMap[status] || 'info'
    },

    // 新增：获取转换状态类型
    getConversionStatusType(status) {
      const statusMap = {
        0: 'info',    // 未转换
        1: 'warning', // 转换中
        2: 'success', // 转换完成
        3: 'danger'   // 转换失败
      }
      return statusMap[status] || 'info'
    },

    // 新增：获取发布状态类型
    getPublishStatusType(status) {
      const statusMap = {
        0: 'info',    // 未发布
        1: 'success', // 已发布
        2: 'danger'   // 发布失败
      }
      return statusMap[status] || 'info'
    },

    // 新增：检查文章是否可发布
    isArticlePublishable(article) {
      // 转换状态为2（转换完成）的文章可以发布
      // 支持重复发布，不检查发布状态
      return article.conversion_status === 2
    },

    // 新增：获取发布按钮文本
    getPublishButtonText(article) {
      if (article.publishing) {
        return '发布中...'
      }
      if (article.conversion_status !== 2) {
        return '不可发布'
      }
      return article.status === 1 ? '重新发布' : '发布'
    },

    // 加载表格数据
    async loadTableData() {
      this.loading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'task_list',
            store_id: this.storeId,
            user_id: this.userId,
            training_type: this.activeMainButton,
            page: this.pagination.page,
            page_size: this.pagination.pageSize
          }
        )

        if (response.data.success) {
          this.tableData = response.data.data
          this.pagination.total = response.data.pagination.total
        } else {
          ElMessage.error(response.data.error || '加载数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        ElMessage.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadTableData()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTableData()
    },

    async handleSubmit() {
      if (!this.$refs.formRef) return

      try {
        const valid = await this.$refs.formRef.validate()
        if (!valid) return

        // 检查配额（-1 代表无限配额，不检查）
        const developCount = this.trainingStats.developCount
        if (developCount !== '无限' && this.formData.training_count > parseInt(developCount)) {
          ElMessage.error(`可发条数不足，需要${this.formData.training_count}条，当前仅有${developCount}条`)
          return
        }

        this.submitting = true

        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'create_task',
            store_id: this.storeId,
            user_id: this.userId,
            ...this.formData,
            training_type: this.activeMainButton
          }
        )

        if (response.data.success) {
          ElMessage.success('训练任务创建成功，开始生成文章...')
          this.showAddDialog = false
          this.loadTableData()
          this.loadUserQuota() // 刷新配额信息
        } else {
          ElMessage.error(response.data.error || '创建失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '创建失败'
        ElMessage.error(`创建失败: ${errorMsg}`)
      } finally {
        this.submitting = false
      }
    },

    // 查看发布
    async handleViewPublish(row) {
      this.selectedTask = row
      this.showArticleDialog = true
      this.articlePagination.page = 1
      this.loadArticleList()
    },

    // 加载文章列表 - 新增转换状态处理
    async loadArticleList() {
      if (!this.selectedTask) return

      this.articleLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'article_list',
            store_id: this.storeId,
            user_id: this.userId,
            task_id: this.selectedTask.id,
            page: this.articlePagination.page,
            page_size: this.articlePagination.pageSize
          }
        )

        if (response.data.success) {
          // 处理转换状态显示
          this.articleList = response.data.data.map(article => ({
            ...article,
            conversion_status_text: this.getConversionStatusText(article.conversion_status),
            publishing: false // 初始化发布状态
          }))
          this.articlePagination.total = response.data.pagination.total
        } else {
          ElMessage.error(response.data.error || '加载文章列表失败')
        }
      } catch (error) {
        console.error('加载文章列表失败:', error)
        ElMessage.error('加载文章列表失败')
      } finally {
        this.articleLoading = false
      }
    },

    // 新增：获取转换状态文本
    getConversionStatusText(status) {
      const statusMap = {
        0: '未转换',
        1: '转换中',
        2: '已转换',
        3: '转换失败'
      }
      return statusMap[status] || '未知'
    },

    handleArticleSizeChange(size) {
      this.articlePagination.pageSize = size
      this.articlePagination.page = 1
      this.loadArticleList()
    },

    handleArticleCurrentChange(page) {
      this.articlePagination.page = page
      this.loadArticleList()
    },

    // 文章选择变化
    handleArticleSelectionChange(selection) {
      this.selectedArticles = selection
    },

    // 编辑文章
    handleEditArticle(row) {
      this.editForm = {
        article_id: row.id,
        title: row.title,
        content: row.content
      }
      this.showEditDialog = true
    },

    // 保存文章
    async handleSaveArticle() {
      if (!this.$refs.editFormRef) return

      try {
        const valid = await this.$refs.editFormRef.validate()
        if (!valid) return

        this.savingArticle = true

        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'update_article',
            store_id: this.storeId,
            user_id: this.userId,
            ...this.editForm
          }
        )

        if (response.data.success) {
          ElMessage.success('文章保存成功')
          this.showEditDialog = false
          this.loadArticleList()
        } else {
          ElMessage.error(response.data.error || '保存失败')
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || '保存失败'
        ElMessage.error(`保存失败: ${errorMsg}`)
      } finally {
        this.savingArticle = false
      }
    },

    // 发布单篇文章 - 修复：支持重复发布
    async handlePublishArticle(row) {
      // 检查文章是否可发布
      if (!this.isArticlePublishable(row)) {
        ElMessage.warning('文章格式转换未完成，暂时无法发布')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要发布文章 "${row.title}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        // 设置发布中状态
        row.publishing = true

        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'publish_articles',
            store_id: this.storeId,
            user_id: this.userId,
            article_ids: [row.id],
            training_type: this.selectedTask.training_type
          }
        )

        if (response.data.success) {
          ElMessage.success(response.data.message)
          // 重新加载文章列表以更新状态
          this.loadArticleList()
          this.loadUserQuota() // 刷新配额信息
        } else {
          ElMessage.error(response.data.error || '发布失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.error || error.message || '发布失败'
          ElMessage.error(`发布失败: ${errorMsg}`)
        }
      } finally {
        // 清除发布中状态
        row.publishing = false
      }
    },

    // 批量发布 - 修复：只发布可发布的文章
    async handleBatchPublish() {
      if (this.selectedArticles.length === 0) {
        ElMessage.warning('请选择要发布的文章')
        return
      }

      // 过滤出可发布的文章
      const publishableArticles = this.selectedArticles.filter(article => 
        this.isArticlePublishable(article)
      )

      if (publishableArticles.length === 0) {
        ElMessage.warning('选中的文章均未完成格式转换，无法发布')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要发布选中的 ${publishableArticles.length} 篇文章吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const articleIds = publishableArticles.map(article => article.id)

        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'publish_articles',
            store_id: this.storeId,
            user_id: this.userId,
            article_ids: articleIds,
            training_type: this.selectedTask.training_type
          }
        )

        if (response.data.success) {
          ElMessage.success(response.data.message)
          this.selectedArticles = []
          this.loadArticleList()
          this.loadUserQuota() // 刷新配额信息
        } else {
          ElMessage.error(response.data.error || '发布失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          const errorMsg = error.response?.data?.error || error.message || '发布失败'
          ElMessage.error(`发布失败: ${errorMsg}`)
        }
      }
    },

    // 图片上传处理
    handleImageAdd(pos, file) {
      // 这里实现图片上传逻辑
      // 由于图片上传需要具体的后端接口，这里只提供框架
      const formData = new FormData()
      formData.append('image', file)

      // 模拟上传
      setTimeout(() => {
        const url = URL.createObjectURL(file)
        this.$refs.mdEditor.$img2Url(pos, url)
      }, 1000)
    },

    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除任务 "${row.task_name}" 吗？此操作会删除所有关联的文章。`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await axios.post(
          `${config.BASE_URL}/store/api/training_management.php`,
          {
            action: 'delete_task',
            store_id: this.storeId,
            user_id: this.userId,
            task_id: row.id
          }
        )

        if (response.data.success) {
          ElMessage.success('删除成功')
          this.loadTableData()
        } else {
          ElMessage.error(response.data.error || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败')
        }
      }
    },

    // 工具函数
    formatTime(timeStr) {
      if (!timeStr) return ''
      const time = new Date(timeStr)
      return time.getFullYear() + '-' +
        String(time.getMonth() + 1).padStart(2, '0') + '-' +
        String(time.getDate()).padStart(2, '0')
    }
  },
  mounted() {
    // 初始化加载数据
    this.loadTableData()
    this.loadUserQuota()
  }
}
</script>

<style scoped lang="scss">
.ai-preparation-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.sub-button-group {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
}

.sub-button {
  padding: 10px 20px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: #e0e6ff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &.orange-button {
    background: #ff7048;
    border-color: #ff7048;
    color: #fff;

    &:hover {
      background: #e55a3a;
      border-color: #e55a3a;
      transform: translateY(-1px);
    }
  }
}

.content-area {
  min-height: 400px;
}

.table-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;
}

.info-bar {
  display: flex;
  gap: 32px;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(255, 112, 72, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(255, 112, 72, 0.2);

  .info-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .info-label {
      color: #e0e6ff;
      font-size: 14px;
    }

    .info-value {
      color: #ff7048;
      font-weight: 600;
      font-size: 14px;
    }
  }
}

.data-table {
  background: transparent;

  :deep(.el-table) {
    background: transparent;

    th {
      background: transparent !important;
      color: #e0e6ff !important;
      border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
    }

    tr {
      background: rgba(255, 255, 255, 0.05);

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  .type-tag {
    background: rgba(255, 112, 72, 0.2);
    border: 1px solid rgba(255, 112, 72, 0.3);
    color: #ff7048;
  }

  .keywords-content {
    color: #e0e6ff;
    line-height: 1.4;
  }

  .count-text {
    color: #ff7048;
    font-weight: 500;
  }

  .action-btn {
    margin: 2px;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 20px 0;
}

.unit-text {
  margin-left: 8px;
  color: #e0e6ff;
}

// 配额信息样式
.quota-info {
  background: rgba(255, 112, 72, 0.1);
  border: 1px solid rgba(255, 112, 72, 0.2);
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;

  .quota-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .quota-label {
      color: #e0e6ff;
      font-size: 14px;
    }

    .quota-value {
      color: #ff7048;
      font-weight: 600;
      font-size: 14px;
    }
  }
}

// 文章相关样式
.article-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;

  .article-stats {
    display: flex;
    align-items: center;
    gap: 16px;

    span {
      color: #e0e6ff;
      font-size: 14px;
    }
  }
}

.article-table {
  :deep(.el-table) {
    background: transparent;

    th,
    tr,
    td {
      background: transparent !important;
      color: #e0e6ff !important;
    }

    .article-title {
      color: #e0e6ff;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: #ff7048;
      }
    }
  }

  :deep(.el-table) {
    .el-button {
      margin: 2px;
    }
    
    .el-button--primary:disabled {
      background: #a0a0a0 !important;
      border-color: #a0a0a0 !important;
      cursor: not-allowed;
    }
  }
  
}

// 编辑容器样式
.edit-container {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .edit-form {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
    padding: 16px;
  }

  .editor-container {
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    overflow: hidden;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .ai-preparation-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .sub-button-group {
    gap: 8px;

    .sub-button {
      padding: 8px 16px;
      font-size: 13px;
    }
  }

  .info-bar {
    flex-direction: column;
    gap: 12px;
  }

  .edit-container {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }

  .sub-button-group {
    justify-content: center;
  }
}
</style>

<style>
/* 全局样式 - 修复对话框问题 */
.training-dialog .el-dialog,
.article-dialog .el-dialog,
.edit-dialog .el-dialog {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 112, 72, 0.3);
  border-radius: 16px;
}

.training-dialog .el-dialog__header,
.article-dialog .el-dialog__header,
.edit-dialog .el-dialog__header {
  margin: 0 !important;
  padding: 20px !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.2) !important;
}

.training-dialog .el-dialog__title,
.article-dialog .el-dialog__title,
.edit-dialog .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
  font-size: 18px;
}

.training-dialog .el-dialog__body,
.article-dialog .el-dialog__body,
.edit-dialog .el-dialog__body {
  padding: 20px !important;
  background: transparent !important;
}

.training-dialog .el-dialog__footer,
.article-dialog .el-dialog__footer,
.edit-dialog .el-dialog__footer {
  border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
  padding: 16px 20px !important;
  margin: 0 !important;
}

/* 表单样式 */
.training-dialog .el-form-item__label {
  color: #e0e6ff !important;
  font-weight: 500;
}

.training-dialog .el-input__inner {
  color: #ffffff !important;

}


.training-dialog .el-textarea__inner {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.training-dialog .el-input__inner::placeholder,
.training-dialog .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.5) !important;
}

.training-dialog .el-input__wrapper:hover,
.training-dialog .el-textarea__inner:hover {
  border-color: #ff7048 !important;
}

.training-dialog .el-input__wrapper.is-focus,
.training-dialog .el-textarea__inner:focus {
  border-color: #ff7048 !important;
  box-shadow: 0 0 0 1px #ff7048 !important;
}

/* 选择器样式 */
.full-width-select {
  width: 100%;
}

.training-dialog .el-select .el-input__inner {
  color: #ffffff !important;
}

.training-dialog .el-select .el-select__caret {
  color: #ff7048 !important;
}

/* 单选组样式 */
.training-dialog .el-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.training-dialog .el-radio {
  color: #e0e6ff !important;
}

.training-dialog .el-radio__input.is-checked .el-radio__inner {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.training-dialog .el-radio__input.is-checked+.el-radio__label {
  color: #ff7048 !important;
}

/* 开关样式 */
.training-dialog .el-switch.is-checked .el-switch__core {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

/* 按钮样式 */
.training-dialog .el-button,
.article-dialog .el-button,
.edit-dialog .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.training-dialog .el-button--primary,
.article-dialog .el-button--primary,
.edit-dialog .el-button--primary {
  background: #ff7048 !important;
  border-color: #ff7048 !important;
}

.training-dialog .el-button--primary:hover,
.article-dialog .el-button--primary:hover,
.edit-dialog .el-button--primary:hover {
  background: #e55a3a !important;
  border-color: #e55a3a !important;
  transform: translateY(-1px);
}

/* Markdown 编辑器样式 */
.edit-dialog .v-note-wrapper {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.edit-dialog .v-note-wrapper .v-note-op {
  background: rgba(255, 255, 255, 0.08) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.edit-dialog .v-note-wrapper .v-note-op .op-icon {
  color: #e0e6ff !important;
}

.edit-dialog .v-note-wrapper .v-note-op .op-icon:hover {
  color: #ff7048 !important;
}

.edit-dialog .v-note-wrapper .v-note-edit {
  background: rgba(255, 255, 255, 0.03) !important;
}

.edit-dialog .v-note-wrapper .v-note-edit .input-area {
  background: transparent !important;
  color: #e0e6ff !important;
}

.edit-dialog .v-note-wrapper .v-note-show {
  background: rgba(255, 255, 255, 0.03) !important;

}

.edit-dialog .v-note-wrapper .v-note-show .markdown-body {
  background: transparent !important;
  color: #e0e6ff !important;
}

/* 表格样式覆盖 */
.el-table {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(255, 255, 255, 0.05) !important;
  --el-table-current-row-bg-color: rgba(255, 112, 72, 0.1) !important;
  --el-table-header-text-color: #e0e6ff !important;
  --el-table-text-color: #e0e6ff !important;
  --el-table-border-color: rgba(255, 112, 72, 0.3) !important;
}

.el-table th,
.el-table tr,
.el-table td {
  background: transparent !important;
  color: #e0e6ff !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.1) !important;
}

.el-table th {
  border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
}

.el-table .el-table__inner-wrapper::before {
  background-color: rgba(255, 112, 72, 0.3) !important;
}
</style>